import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import { StringParam, useQueryParams, NumberParam, BooleanParam, ArrayParam } from 'use-query-params';
import { encodeDate, unpackToDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router';
import { pick, orderBy, merge } from 'lodash';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { CollapsibleSideBar } from '../../../common/CollapsibleSideBar';
import { CommonScreenSkeleton } from '../../../common/CommonScreenSkeleton';
import { RostersSideBar } from './sidebar/RostersSideBar';
import { RosterMainSection } from './main-section/RosterMainSection';
import {
  rostersFilteredByEndDate,
  rostersFilteredByEndTime,
  rostersFilteredByProgram,
  rostersFilteredBySearch,
  rostersFilteredByStartDate,
  rostersFilteredByStartTime,
  rostersFilteredByStatus,
  rostersFilteredByType,
  rostersFilteredByVolunteersNeeded,
  isRosterCompleted,
  rosterFilteredByTag,
  rosterFilteredByDay,
  rosterFilteredByRosterTypes,
  rostersFilteredByActivity,
} from './main-section/filters/roster-filter-functions';
import { useCancelActivityFetch } from './main-section/roster/roster-functions';
import { GET_ACTIVITY_IDS } from './rosters-sidebar-model.gql';
import { useSidebarData } from './sidebar/useSidebarData';
import { SelectActivityCancellationReasonDialog } from '../activities/activity-timeline/common/SelectActivityCancellationReasonDialog';
import { useSnackbar } from '../../../global/config/useSnackbar';
import { MobileIndividualRosterScreen } from './MobileIndividualRosterScreen';
import { useUser } from '../../../global/auth/useUser';
import { TabletRoster } from './tablet-roster/TabletRoster';
import { useSharedRosterData } from './state';
import { useDeepEffect } from '../../../hooks/useDeepEffect';
import { RosterSettingDialog } from './settings/RosterSettingDialog';
import { RosterToolbar } from './single-roster/RosterToolbar';
import { ProgramsContext } from '../../../global/programs-sell/ProgramsContext';
import { useCampfireLazyQuery } from '../../../global/network/useCampfireLazyQuery';
import { RostersGetActivityIds, RostersGetActivityIdsVariables } from './__generated__/RostersGetActivityIds';
import { initFilters } from './main-section/filters/RosterFilters';
import { useSharedSessionResults } from './main-section/roster/RosterHeader';
import { SessionContext } from '../../../global/auth/SessionContext';
import { useOrgInfo } from '../../../global/auth/use-org-info/use-org-info';
import { getMenuLabel } from '../../../global/auth/use-menu-labels/use-menu-labels';
import { EditRosterTemplateDialog } from './roster-templates-screen/EditRosterTemplateDialog';
import { RosterTemplateFormValues } from './roster-templates-screen/RosterTemplateForm';

export const SMALL_ORG_DAYS_LIMIT = 30;
export const LARGE_ORG_DAYS_LIMIT = 14;

export const IndividualRosterScreen = () => {
  const { isMd, isMobile } = useCampfireTheme();
  const [showFilters, setShowFilters] = useState(false);
  const [showCancelActivityDialog, setShowCancelActivityDialog] = useState(false);
  const [historyViewSet, setHistoryViewSet] = useState(false);
  const [isOpenSetting, setIsOpenSetting] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [rosterRefetchTrigger, setRosterRefetchTrigger] = useState(0);
  const [editingRosterTemplateId, setEditingRosterTemplateId] = useState<string>();
  const [isBackgroundRefresh, setIsBackgroundRefresh] = useState(false);
  const { setRosterData } = useSharedRosterData();
  const history = useHistory();
  const { setSnackbar } = useSnackbar();
  const cancelActivity = useCancelActivityFetch();
  const session = React.useContext(SessionContext);
  const isLargeOrg = session?.user?.orgInfo.isLargeOrg;
  const { selectedPrograms } = React.useContext(ProgramsContext);
  const { setIsExpanded } = useSharedSessionResults();
  const [query, setQuery] = useQueryParams({
    activityDate: StringParam,
    activityId: StringParam,
    activityFilterId: StringParam,
    programId: StringParam,
    activityType: StringParam,
    activityStatus: StringParam,
    volunteersNeeded: NumberParam,
    startTime: StringParam,
    endTime: StringParam,
    startDate: StringParam,
    endDate: StringParam,
    onHistory: BooleanParam,
    q: StringParam,
    view: StringParam,
    rosterType: StringParam,
    days: ArrayParam,
    tags: ArrayParam,
  });

  const defaultStartDate = React.useRef(encodeDate(DateTime.local()));
  const defaultEndDate = React.useMemo(() => {
    const numOfDays = isLargeOrg ? LARGE_ORG_DAYS_LIMIT : SMALL_ORG_DAYS_LIMIT;
    return encodeDate(DateTime.local().plus({ days: numOfDays }));
  }, [isLargeOrg]);
  const numberOfDays = React.useMemo(() => {
    return isLargeOrg ? LARGE_ORG_DAYS_LIMIT : SMALL_ORG_DAYS_LIMIT;
  }, [isLargeOrg]);

  React.useEffect(() => {
    if (!query.startDate) {
      setQuery({
        startDate: defaultStartDate.current,
      });
    }
  }, [query.startDate]);

  React.useEffect(() => {
    if (!query.endDate) {
      setQuery({
        endDate: defaultEndDate,
      });
    }
  }, [query.endDate]);

  const { programId: selectedProgramId, activityId: selectedActivityId } = query;

  const includeInactiveActivities = query.activityStatus !== 'active' && query.activityStatus !== undefined;

  const [queryActivityIds, vmActivityIds] = useCampfireLazyQuery<RostersGetActivityIds, RostersGetActivityIdsVariables>(
    GET_ACTIVITY_IDS
  );

  const activityIds = useMemo(() => {
    return vmActivityIds.data?.vm.programs.flatMap((program) =>
      program.activities.map((activity) => activity.activityId)
    );
  }, [vmActivityIds.data]);

  const { data, rosters, loading, refetch: refetchSidebarData } = useSidebarData(
    activityIds,
    query.startDate ?? undefined,
    query.endDate ?? undefined
  );

  React.useEffect(() => {
    queryActivityIds({
      variables: {
        isActive: includeInactiveActivities,
        programIds: selectedPrograms.length > 0 ? selectedPrograms : undefined,
      },
    });
  }, []);

  React.useEffect(() => {
    if (selectedPrograms.length === 1) {
      const defaultQuery = merge(initFilters(isLargeOrg), query);
      setQuery({
        ...defaultQuery,
        programId: selectedPrograms[0],
      });
    }
  }, []);

  useDeepEffect(() => {
    if (selectedPrograms.length > 0 && selectedActivityId !== 'all') {
      queryActivityIds({
        variables: {
          isActive: includeInactiveActivities,
          programIds: selectedProgramId === 'all' || selectedProgramId === undefined ? undefined : [selectedProgramId],
        },
      });
    }
  }, [includeInactiveActivities, selectedProgramId]);

  const setSearchFilter = (value: string) =>
    setQuery({
      ...query,
      q: value,
    });

  const {
    user: { userIdentityService },
  } = useUser();
  const userIsAdmin = userIdentityService.isVmAdmin;
  const userIsActivityLeader = (activityId: string) => userIdentityService.isLeaderOfActivity(activityId);
  const userIsPmOrAdmin = (programId: string) => userIsAdmin || userIdentityService.isManagerOfProgram(programId);

  // Filter rosters by user permissions
  const filteredByPermissions = useMemo(() => {
    if (!rosters) return undefined;
    return rosters.filter((r) => userIsPmOrAdmin(r.programId) || userIsActivityLeader(r.activityId));
  }, [rosters, userIsAdmin]);

  const selectedActivity = useMemo(() => data?.vm.activities?.find((a) => a.activityId === query.activityId), [
    data,
    query,
  ]);

  const filteredRostersList = useMemo(() => {
    const filteredBySearch = rostersFilteredBySearch(filteredByPermissions || [], query.q);
    const filteredByProgram = rostersFilteredByProgram(filteredBySearch, query.programId);
    const filteredByActivity = rostersFilteredByActivity(filteredByProgram, query.activityFilterId);
    const filteredByType = rostersFilteredByType(filteredByActivity, query.activityType);
    const filteredByStatus = rostersFilteredByStatus(filteredByType, query.activityStatus);
    const filteredByStartTime = rostersFilteredByStartTime(filteredByStatus, query.startTime);
    const filteredByEndTime = rostersFilteredByEndTime(filteredByStartTime, query.endTime);
    const filteredByStartDate = rostersFilteredByStartDate(filteredByEndTime, query.startDate);
    const filteredByEndDate = rostersFilteredByEndDate(filteredByStartDate, query.endDate);
    const filteredByVolsNeeded = rostersFilteredByVolunteersNeeded(filteredByEndDate, query.volunteersNeeded);
    const filteredByTag = rosterFilteredByTag(filteredByVolsNeeded, query.tags || []);
    const filteredByDay = rosterFilteredByDay(filteredByTag, query.days || []);
    const filteredByRosterType = rosterFilteredByRosterTypes(filteredByDay, query.rosterType || 'all');

    const dateOrder = query.onHistory ? 'desc' : 'asc';

    return orderBy(filteredByRosterType, ['activityDate', 'startTime', 'activityName'], [dateOrder, dateOrder, 'asc']);
  }, [filteredByPermissions, query]);

  useDeepEffect(() => {
    if (isMobile || isMd || filteredRostersList.length === 0) return;
    if (!loading && onHistory && query.activityId !== filteredRostersList[0].activityId && !historyViewSet) {
      setQuery({
        ...pick(filteredRostersList[0], ['activityId', 'activityDate']),
      });
      setHistoryViewSet(true);
    }
    if (!loading && Object.values(query).every((queryParams) => !queryParams) && !query.onHistory) {
      setQuery({
        ...pick(filteredRostersList[0], ['activityId', 'activityDate']),
      });
    }
  }, [filteredRostersList, query]);

  useDeepEffect(() => {
    const isRosterInList = filteredRostersList.some(
      ({ activityId, activityDate }) => activityId === query.activityId && activityDate === query.activityDate
    );

    if (isMobile || isMd || isRosterInList) return;
    setQuery({
      ...pick(filteredRostersList[0], ['activityId', 'activityDate']),
    });
  }, [filteredRostersList, query]);

  const selectedRoster = useMemo(() => {
    const roster = filteredRostersList.find(
      (r) => r.activityId === query.activityId && r.activityDate === query.activityDate
    );
    if (isMobile || isMd) {
      return roster;
    }
    return roster || filteredRostersList[0];
  }, [query, filteredRostersList, isMobile, isMd]);

  const rosterTemplate = useMemo(() => {
    return selectedActivity?.rosterTemplates?.find((rt) => {
      return (
        unpackToDate(rt.rosterDate).toFormat('yyyy-MM-dd') ===
        unpackToDate(query.activityDate ?? '').toFormat('yyyy-MM-dd')
      );
    });
  }, [selectedActivity, query]);

  useEffect(() => {
    setRosterData(data);
  }, [data]);

  useEffect(() => {
    if (!loading) setIsBackgroundRefresh(false);
  }, [loading]);

  const handleEditRosterTemplate = () => {
    if (!rosterTemplate) return;
    setEditingRosterTemplateId(rosterTemplate.rosterTemplateId);
  };

  const refreshAfterRosterTemplateEdit = () => {
    setIsBackgroundRefresh(true);
    refetchSidebarData();
    setRefetchTrigger((prev) => prev + 1);
    setRosterRefetchTrigger((prev) => prev + 1);
  };

  const handleRosterTemplateSaved = (values?: RosterTemplateFormValues) => {
    setEditingRosterTemplateId(undefined);
    if (!values) return;

    const savedDate = values.rosterDates[0] ? encodeDate(DateTime.fromJSDate(values.rosterDates[0])) : undefined;
    if (savedDate && (savedDate !== query.activityDate || values.activityId !== query.activityId)) {
      setQuery({ activityId: values.activityId, activityDate: savedDate });
    }
    refreshAfterRosterTemplateEdit();
  };

  const handleRosterTemplateDeleted = () => {
    setEditingRosterTemplateId(undefined);
    refreshAfterRosterTemplateEdit();
  };

  const handleRosterTemplateRejected = () => {
    if (!data || !query.activityId || !query.activityDate) return;

    // Update the local data to remove the rejected roster template
    const updatedData = {
      ...data,
      vm: {
        ...data.vm,
        activities: data.vm.activities.map((activity) => {
          if (activity.activityId === query.activityId) {
            return {
              ...activity,
              rosterTemplates: activity.rosterTemplates.filter(
                (template) => template.rosterDate !== query.activityDate
              ),
            };
          }
          return activity;
        }),
      },
    };

    setRosterData(updatedData);
    // Navigate away from the rejected date
    setQuery({ activityDate: undefined, activityId: undefined });
  };

  const onHistoryClick = () => {
    if (query.onHistory) {
      history.goBack();
    } else {
      setQuery({ activityDate: undefined, activityId: undefined });
      setQuery(
        {
          activityStatus: 'completed',
          startDate: encodeDate(DateTime.local().minus({ days: numberOfDays })),
          endDate: encodeDate(DateTime.local()),
          onHistory: true,
        },
        'push'
      );
      setHistoryViewSet(false);
    }
  };

  const onLoadNext = () => {
    if (query.endDate) {
      setQuery({
        ...query,
        startDate: query.endDate,
        endDate: encodeDate(
          unpackToDate(query.endDate).plus({
            days: numberOfDays,
          })
        ),
      });
    }
  };

  const onLoadPrev = () => {
    if (query.startDate) {
      setQuery({
        ...query,
        startDate: encodeDate(
          unpackToDate(query.startDate).minus({
            days: numberOfDays,
          })
        ),
        endDate: query.startDate,
      });
    }
  };

  const { onHistory } = query;

  useEffect(() => {
    if (onHistory) {
      setShowFilters(false);
    }
  }, [onHistory]);

  const onSetShowFilterClick = () => {
    if (!showFilters) {
      setQuery({ ...query, onHistory: false });
    }
    setShowFilters(!showFilters);
  };

  const handleOpenSetting = () => {
    setIsOpenSetting(true);
  };
  const handleCloseSetting = () => {
    setIsOpenSetting(false);
  };

  const handleRefetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  // Get custom label for rosters
  const org = useOrgInfo();
  const rostersLabel = getMenuLabel('managementRosters', org?.menuLabels);

  return (
    <>
      {(loading && !isBackgroundRefresh) || vmActivityIds.loading ? (
        <CommonScreenSkeleton />
      ) : isMobile ? (
        <MobileIndividualRosterScreen
          selectedRoster={selectedRoster}
          loading={loading}
          rosterTemplate={rosterTemplate}
          rosters={filteredRostersList}
          handleEditRosterTemplate={handleEditRosterTemplate}
          cancelActivity={() => setShowCancelActivityDialog(true)}
          setSearchFilter={setSearchFilter}
          handleReactivateActivityRefetch={handleRefetch}
          rosterRefetchTrigger={rosterRefetchTrigger}
          query={query}
          onSetQuery={setQuery}
          isRosterCompleted={isRosterCompleted(selectedRoster)}
          onHistoryClick={onHistoryClick}
          showFilters={showFilters}
          setShowFilters={onSetShowFilterClick}
          loadPrev={onLoadPrev}
          loadNext={onLoadNext}
        />
      ) : isMd ? (
        <TabletRoster
          loading={loading}
          onHistoryClick={onHistoryClick}
          selectedRoster={selectedRoster}
          setSearchFilter={setSearchFilter}
          showFilters={showFilters}
          setShowFilters={onSetShowFilterClick}
          handleEditRosterTemplate={handleEditRosterTemplate}
          setShowCancelActivityDialog={setShowCancelActivityDialog}
          rosters={filteredRostersList}
          rosterTemplate={rosterTemplate}
          handleReactivateActivityRefetch={handleRefetch}
          rosterRefetchTrigger={rosterRefetchTrigger}
          query={query}
          onSetQuery={setQuery}
          isRosterCompleted={isRosterCompleted(selectedRoster)}
          loadPrev={onLoadPrev}
          loadNext={onLoadNext}
        />
      ) : (
        <Box width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} overflow={'hidden'}>
          <RosterToolbar
            title={'Rosters'}
            query={query.q || ''}
            setQueryValue={setSearchFilter}
            isFiltering={showFilters}
            setFiltering={onSetShowFilterClick}
            isHistory={Boolean(query.onHistory)}
            setHistory={onHistoryClick}
            onSettings={handleOpenSetting}
            onTemplate={handleEditRosterTemplate}
            onCancel={() => setShowCancelActivityDialog(true)}
            onViewChange={(view) => {
              setQuery({ view });
            }}
            currentView='single'
            showCancel={false}
            showTemplate={Boolean(rosterTemplate)}
            originalLabel={rostersLabel.label}
            customLabel={rostersLabel.customLabel}
          />
          <Box
            position={'relative'}
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-end'}
            overflow={'hidden'}
            flex={1}
            alignItems='stretch'
            padding={'0px 0px 0px 30px'}
          >
            <RosterMainSection
              loading={loading}
              showFilters={showFilters}
              query={query}
              onSetQuery={setQuery}
              rosterTemplate={rosterTemplate}
              handleEditRosterTemplate={handleEditRosterTemplate}
              hasRosters={!!(filteredByPermissions && filteredByPermissions.length >= 1)}
              handleReactivateActivityRefetch={handleRefetch}
              rosterRefetchTrigger={rosterRefetchTrigger}
              isRosterCompleted={isRosterCompleted(selectedRoster)}
              activityStatus={selectedRoster?.activityStatus}
              setShowCancelActivityDialog={setShowCancelActivityDialog}
              onRosterTemplateRejected={handleRosterTemplateRejected}
            />
            <CollapsibleSideBar setSiblingExpanded={setIsExpanded}>
              <RostersSideBar
                rosters={filteredRostersList}
                loadNext={onLoadNext}
                loadPrev={onLoadPrev}
                refetchTrigger={refetchTrigger}
              />
            </CollapsibleSideBar>
          </Box>
        </Box>
      )}
      {selectedActivity?.name && query.activityDate && query.activityId ? (
        <SelectActivityCancellationReasonDialog
          open={showCancelActivityDialog}
          handleClose={() => setShowCancelActivityDialog(false)}
          handleSelection={({
            activityCancellationReasonId,
            description,
            addedAttachments,
            attachmentCreationTokens,
          }) => {
            cancelActivity
              .run({
                activityId: query.activityId ?? '',
                activityDate: query.activityDate ?? '',
                activityCancellationReasonId,
                description: JSON.stringify(description),
                addedAttachments,
                attachmentCreationTokens,
              })
              .then((res) => {
                if (!res.ok) {
                  setSnackbar({
                    open: true,
                    message: 'Unable to cancel activity',
                    variant: 'error',
                  });
                  return;
                }

                setSnackbar({
                  open: true,
                  message: 'Activity cancelled',
                  variant: 'success',
                });

                handleRefetch();
                setShowCancelActivityDialog(false);
              })
              .catch(() =>
                setSnackbar({
                  open: true,
                  message: 'Unable to cancel activity',
                  variant: 'error',
                })
              );
          }}
          activityName={selectedActivity.name}
          activityDate={query.activityDate}
        />
      ) : null}
      {isOpenSetting && <RosterSettingDialog open={isOpenSetting} onClose={handleCloseSetting} />}
      {editingRosterTemplateId && (
        <EditRosterTemplateDialog
          rosterTemplateId={editingRosterTemplateId}
          onClose={() => setEditingRosterTemplateId(undefined)}
          onSaved={handleRosterTemplateSaved}
          onDeleted={handleRosterTemplateDeleted}
        />
      )}
    </>
  );
};
